import { Link, useParams } from "react-router-dom";
import { Placeholder } from "../components/Placeholder";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { LEADERS, getLeader } from "../lib/leadership";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function LeadershipMember() {
  const { slug = "" } = useParams<{ slug: string }>();
  const l = getLeader(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!l) {
    return (
      <section className="alum-hero">
        <div className="container">
          <div className="ah-eyebrow">404 · Leadership</div>
          <h1 className="ah-name">Profile not found.</h1>
          <p><Link to={ROUTES.about}>← Back to about</Link></p>
        </div>
      </section>
    );
  }

  const others = LEADERS.filter((x) => x.slug !== l.slug).slice(0, 4);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`LEADERSHIP · ${l.term.toUpperCase()}`}
        title={l.name}
        back={{ label: "About BIET", to: ROUTES.about }}
      />

      <section className="alum-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb" style={{ color: "var(--text-muted)" }}>
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.about}>About</Link>
            <span aria-hidden>/</span>
            <span>Leadership</span>
          </nav>

          <div className="ah-grid">
            <div className="ah-portrait">
              <Placeholder label={"PORTRAIT · " + l.name.toUpperCase()} aspect="3/4" />
            </div>
            <div>
              <div className="ah-eyebrow">{l.role} · {l.term}</div>
              <h1 className="ah-name">{l.name}</h1>
              <div className="ah-role">{l.portfolio.slice(0, 3).join(" · ")}</div>
              <div className="ah-quote">"{l.vision}"</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>Biography</h2>
              {l.bio.map((p, i) => <p key={i}>{p}</p>)}

              <h2>Career arc</h2>
              <ul className="alum-journey">
                {l.career.map((j, i) => (
                  <li key={i}>
                    <div className="yr">{j.year}</div>
                    <div className="tx">{j.what}</div>
                  </li>
                ))}
              </ul>

              <h2>Portfolio</h2>
              <div className="center-focus">
                {l.portfolio.map((p, i) => <span key={i}>{p}</span>)}
              </div>

              <div className="alum-advice">
                <span className="lbl">Vision · in {l.role.split(" · ")[0]}'s own words</span>
                "{l.vision}"
              </div>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Role</div>
                <div className="dmc-value">{l.role}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Term</div>
                <div className="dmc-value">{l.term}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Email</div>
                <div className="dmc-value"><a href={`mailto:${l.contact.email}`}>{l.contact.email}</a></div>
              </div>
              {l.contact.phone && (
                <div className="dmc-block">
                  <div className="dmc-label">Phone</div>
                  <div className="dmc-value">{l.contact.phone}</div>
                </div>
              )}
              {l.contact.office && (
                <div className="dmc-block">
                  <div className="dmc-label">Office</div>
                  <div className="dmc-value">{l.contact.office}</div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More leadership</div>
              <h2>Other members of the administration</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.leadership}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    {o.term.match(/\d{4}/) && (
                      <>
                        <span className="er-day">'{o.term.match(/\d{4}/)![0].slice(2)}</span>
                        <span className="er-month">SINCE</span>
                      </>
                    )}
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.role.split(",")[0]}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.term}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
